import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Empty, message, Select, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TabPane, Tabs } from '../../components/index';
import {
  HealthDependency,
  HealthGroup,
  useCheckAllDependenciesMutation,
  useCheckDependencyMutation,
  useGetIntegrationHealthQuery,
} from '../../services/integrationHealthApi';
import { GroupCards } from './GroupCards';
import { LogsDrawer } from './LogsDrawer';

const DEFAULT_REFRESH_MINUTES = 1;

// Quanto tempo depois de disparar "verificar todos" a tela relê (o worker roda
// os probes de forma assíncrona; estas releituras trazem o resultado).
const RECHECK_REFETCH_DELAYS_MS = [4000, 10000];

const EXTERNAL_GROUP = 'external_agencies';
const PARTNERS_GROUP = 'partners_inbound';

export function HealthOverview() {
  const [refreshMinutes, setRefreshMinutes] = useState<number>(
    DEFAULT_REFRESH_MINUTES
  );
  // Pausa o polling quando a aba não está visível (skipPollingIfUnfocused só
  // existe no RTK Query 2.0; aqui o efeito é feito na mão).
  const [isVisible, setIsVisible] = useState(
    document.visibilityState === 'visible'
  );
  const [lastRefreshTime, setLastRefreshTime] = useState<string>();
  const [selectedDependency, setSelectedDependency] =
    useState<HealthDependency | null>(null);
  // Conjunto, não chave única: duas verificações podem estar em voo ao mesmo
  // tempo (a do PSP leva ~20s), e uma guardando só a última fazia o spinner da
  // outra desaparecer.
  const [checkingKeys, setCheckingKeys] = useState<Set<string>>(new Set());
  const [isRechecking, setIsRechecking] = useState(false);

  const [checkDependency] = useCheckDependencyMutation();
  const [checkAll] = useCheckAllDependenciesMutation();

  // Os setTimeout de releitura precisam ser cancelados: sem isso, navegar para
  // outra tela depois de "Atualizar" dispara setState em componente desmontado
  // e deixa duas requisições órfãs.
  const recheckTimeouts = useRef<number[]>([]);

  const clearRecheckTimeouts = useCallback(() => {
    recheckTimeouts.current.forEach((id) => window.clearTimeout(id));
    recheckTimeouts.current = [];
  }, []);

  useEffect(() => clearRecheckTimeouts, [clearRecheckTimeouts]);

  useEffect(() => {
    const onVisibility = () =>
      setIsVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const pollingInterval =
    isVisible && refreshMinutes ? refreshMinutes * 60000 : 0;

  const { data, isLoading, isFetching, isError, refetch } =
    useGetIntegrationHealthQuery(undefined, { pollingInterval });

  useEffect(() => {
    if (data) setLastRefreshTime(new Date().toLocaleTimeString('pt-BR'));
  }, [data]);

  const groupsByKey = useMemo(() => {
    const map: Record<string, HealthGroup> = {};
    (data?.groups || []).forEach((group) => {
      map[group.key] = group;
    });
    return map;
  }, [data]);

  async function onCheck(serviceKey: string) {
    setCheckingKeys((current) => new Set(current).add(serviceKey));
    try {
      await checkDependency(serviceKey).unwrap();
      await refetch();
    } catch {
      message.error('Não foi possível verificar agora. Tente novamente.');
    } finally {
      setCheckingKeys((current) => {
        const next = new Set(current);
        next.delete(serviceKey);
        return next;
      });
    }
  }

  // "Atualizar" = verificar todos ao vivo: dispara a checagem no worker e relê
  // a tela em seguida (o resultado chega em alguns segundos).
  async function onCheckAll() {
    clearRecheckTimeouts();
    setIsRechecking(true);
    try {
      await checkAll().unwrap();
      message.success('Verificando todos os serviços...');

      // O botão fica travado até a última releitura: o POST responde 202 na
      // hora, e destravar aí convidava a disparar varreduras concorrentes — cada
      // uma faz cinco autenticações reais contra órgão de governo.
      const lastDelay = Math.max(...RECHECK_REFETCH_DELAYS_MS);
      recheckTimeouts.current = RECHECK_REFETCH_DELAYS_MS.map((delay) =>
        window.setTimeout(() => {
          refetch();
          setLastRefreshTime(new Date().toLocaleTimeString('pt-BR'));
          if (delay === lastDelay) setIsRechecking(false);
        }, delay)
      );
    } catch {
      message.error('Não foi possível iniciar a verificação.');
      setIsRechecking(false);
    }
  }

  if (isLoading) {
    return (
      <Spin tip="Carregando..." style={{ width: '100%', marginTop: 48 }} />
    );
  }

  if (!data) {
    return (
      <Empty description="Não foi possível carregar a saúde das integrações." />
    );
  }

  const controls = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 24px',
      }}
    >
      <div style={{ flex: 1 }} />
      <span style={{ fontSize: 12, color: 'var(--neutral_medium)' }}>
        {isFetching || isRechecking
          ? 'Atualizando...'
          : lastRefreshTime && `Última atualização às ${lastRefreshTime}`}
      </span>
      <Button
        type="text"
        icon={<ReloadOutlined />}
        loading={isRechecking || isFetching}
        disabled={isRechecking}
        onClick={onCheckAll}
      >
        Atualizar
      </Button>
      <Select
        aria-label="Atualização automática"
        value={refreshMinutes}
        optionLabelProp="label"
        onChange={setRefreshMinutes}
        bordered={false}
        dropdownMatchSelectWidth={180}
        style={{ width: 90 }}
      >
        <Select.OptGroup label="Atualização automática">
          <Select.Option value={0} label="Desligada">
            Desligada
          </Select.Option>
          <Select.Option value={1} label="1 min">
            a cada 1 minuto
          </Select.Option>
          <Select.Option value={5} label="5 min">
            a cada 5 minutos
          </Select.Option>
        </Select.OptGroup>
      </Select>
    </div>
  );

  return (
    <>
      {/* Numa tela cuja única função é dizer se as integrações estão de pé,
          congelar o último retrato verde sem avisar é o pior modo de falha
          possível: o dado continua visível (é melhor que nada), mas com a
          ressalva de que ele parou. */}
      {isError && (
        <Alert
          type="warning"
          showIcon
          banner
          message="Não foi possível atualizar a saúde das integrações."
          description={
            lastRefreshTime
              ? `Os dados abaixo são da última leitura bem-sucedida, às ${lastRefreshTime}.`
              : 'Os dados abaixo podem estar desatualizados.'
          }
        />
      )}

      <Tabs
        defaultActiveKey={EXTERNAL_GROUP}
        size="large"
        tabBarExtraContent={controls}
        style={{ background: 'var(--neutral_lightest)' }}
        tabBarStyle={{ paddingLeft: 24, marginBottom: 0 }}
      >
        <TabPane tab="Órgãos anuentes e sistemas do porto" key={EXTERNAL_GROUP}>
          <GroupCards
            group={groupsByKey[EXTERNAL_GROUP]}
            onOpenLogs={setSelectedDependency}
            onCheck={onCheck}
            checkingKeys={checkingKeys}
          />
        </TabPane>
        <TabPane tab="Comunidade portuária" key={PARTNERS_GROUP}>
          <GroupCards
            group={groupsByKey[PARTNERS_GROUP]}
            onOpenLogs={setSelectedDependency}
            onCheck={onCheck}
            checkingKeys={checkingKeys}
          />
        </TabPane>
      </Tabs>

      <LogsDrawer
        serviceKey={selectedDependency?.key ?? null}
        title={selectedDependency?.label ?? ''}
        onClose={() => setSelectedDependency(null)}
      />
    </>
  );
}
